import React, { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import moment from "moment";
import XLSX from "xlsx";
import IconButton from "@material-ui/core/IconButton";
import Checkbox from "@material-ui/core/Checkbox";

import AddIcon from "@material-ui/icons/Add";
import LocalOfferIcon from "@material-ui/icons/LocalOffer";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import ScheduleIcon from "@material-ui/icons/Schedule";
import EventBusyIcon from "@material-ui/icons/EventBusy";
import RefreshIcon from "@material-ui/icons/Refresh";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import CheckIcon from "@material-ui/icons/Check";

import ErrorPage from "commonComponents/errorPage/ErrorPage";
import PageHeader from "designV2/components/PageHeader";
import PageActionButton from "designV2/components/PageActionButton";
import Button from "designV2/components/Button";
import Field from "designV2/components/Field";
import MetricCard from "designV2/components/MetricCard";
import SearchField from "designV2/components/SearchField";
import FilterDropdown from "designV2/components/FilterDropdown";
import NumberRangeFilter from "designV2/components/NumberRangeFilter";
import ActiveFilterChipList from "designV2/components/ActiveFilterChipList";
import TableToolbar from "designV2/components/TableToolbar";
import StatusBadge from "designV2/components/StatusBadge";
import EmptyState from "designV2/components/EmptyState";
import PaginationComponent from "designV2/components/Pagination";
import DialogShell from "designV2/components/DialogShell";
import { MetricsSkeleton, TableSkeleton } from "designV2/components/Skeleton";
import "./promotions.css";

/* ------------------------------------------------------------------ *
 * GraphQL
 *
 * The list, its count and the create/update mutations are the same
 * ones the pre-redesign screen used (getOrderPromotionsByShopAdmin /
 * createOrderPromotionByShopAdmin / updateOrderPromotionByShopAdmin /
 * deleteOrderPromotionByShopAdmin) — no backend changes needed. Search,
 * filtering, sorting and pagination all happen client-side over one
 * fetched window, the same approach ShopApiKeys uses: a shop's
 * promotion codes number in the dozens, not the thousands, so one
 * request comfortably covers them and every control can respond
 * instantly instead of round-tripping the server.
 * ------------------------------------------------------------------ */

const PROMOTION_FIELDS = `
  _id
  condition
  type
  useLimitCount
  from
  to
  percent
  maximumPromotion
  promotionCode
`;

const FETCH_LIMIT = 1000;

// filters is passed as an explicit empty object (every sub-field optional)
// rather than omitted, matching the payload the pre-redesign screen sent
// whenever no filter was set — omitting the argument outright risks a
// schema validation error if the resolver marks it non-null.
const GET_PROMOTIONS = gql`
  query getOrderPromotionsByShopAdmin($limit: Int, $skip: Int) {
    getOrderPromotionsByShopAdmin(
      pagination: { limit: $limit, skip: $skip }
      filters: {}
      sort: { createdAt: -1 }
    ) {
      ${PROMOTION_FIELDS}
    }
    getOrderPromotionsByShopAdminCount(filters: {})
  }
`;

const CREATE_PROMOTION = gql`
  mutation createOrderPromotionByShopAdmin(
    $condition: OrderPromotionCondition!
    $type: promotionTypeEnum!
    $from: Date
    $to: Date
    $percent: Float
    $useLimitCount: Int
    $maximumPromotion: Float
    $promotionCode: String!
  ) {
    createOrderPromotionByShopAdmin(
      data: {
        condition: $condition
        type: $type
        from: $from
        to: $to
        percent: $percent
        useLimitCount: $useLimitCount
        maximumPromotion: $maximumPromotion
        promotionCode: $promotionCode
      }
    ) {
      _id
    }
  }
`;

const UPDATE_PROMOTION = gql`
  mutation updateOrderPromotionByShopAdmin(
    $promotionId: ID!
    $condition: OrderPromotionCondition!
    $type: promotionTypeEnum!
    $from: Date
    $to: Date
    $percent: Float
    $useLimitCount: Int
    $maximumPromotion: Float
    $promotionCode: String!
  ) {
    updateOrderPromotionByShopAdmin(
      promotionId: $promotionId
      data: {
        condition: $condition
        type: $type
        from: $from
        to: $to
        percent: $percent
        useLimitCount: $useLimitCount
        maximumPromotion: $maximumPromotion
        promotionCode: $promotionCode
      }
    ) {
      _id
    }
  }
`;

const DELETE_PROMOTIONS = gql`
  mutation deleteOrderPromotionByShopAdmin($idSet: [ID!]!) {
    deleteOrderPromotionByShopAdmin(idSet: $idSet) {
      _id
    }
  }
`;

/* ------------------------------------------------------------------ *
 * Constants
 * ------------------------------------------------------------------ */

const CONDITION_OPTIONS = [
  { value: "TIMELY", label: "Timely" },
  { value: "FIRST_ORDER", label: "First order" },
  { value: "PERCENTAGE", label: "Percentage" },
];
const CONDITION_LABELS = CONDITION_OPTIONS.reduce((acc, opt) => {
  acc[opt.value] = opt.label;
  return acc;
}, {});

const TYPE_OPTIONS = [
  { value: "PERCENT", label: "Percentage off" },
  { value: "FIXED", label: "Fixed amount off" },
];

const SORT_OPTIONS = [
  { value: "endingSoon", label: "Ending soonest" },
  { value: "codeAsc", label: "Code A-Z" },
  { value: "percentDesc", label: "Discount % high-low" },
  { value: "percentAsc", label: "Discount % low-high" },
  { value: "maxDesc", label: "Max discount high-low" },
  { value: "maxAsc", label: "Max discount low-high" },
];
const SORT_LABELS = SORT_OPTIONS.reduce((acc, opt) => {
  acc[opt.value] = opt.label;
  return acc;
}, {});

const emptyForm = () => ({
  promotionCode: "",
  condition: "TIMELY",
  type: "PERCENT",
  percent: "",
  maximumPromotion: "",
  useLimitCount: "",
  from: "",
  to: "",
});

/* ------------------------------------------------------------------ *
 * Helpers
 * ------------------------------------------------------------------ */

// A promotion has no "status" field of its own — active/scheduled/expired is
// derived from `from`/`to` against the clock. Anything active and ending
// within 72 hours is called out separately so a shop owner notices a code
// is about to lapse instead of finding out from a customer.
const getPromotionState = (promotion) => {
  const now = moment();
  const from = promotion.from ? moment(promotion.from) : null;
  const to = promotion.to ? moment(promotion.to) : null;

  if (to && now.isAfter(to)) {
    return { key: "expired", status: "danger", label: "Expired" };
  }
  if (from && now.isBefore(from)) {
    return { key: "scheduled", status: "info", label: "Scheduled" };
  }
  if (to && to.diff(now, "hours") <= 72) {
    return { key: "active", status: "warning", label: `Ends ${to.fromNow()}` };
  }
  return { key: "active", status: "success", label: "Active" };
};

const formatDiscount = (promotion) =>
  promotion.type === "FIXED"
    ? `$${Number(promotion.maximumPromotion || 0).toFixed(2)} off`
    : `${promotion.percent || 0}% off`;

const formatCap = (promotion) =>
  promotion.type === "FIXED"
    ? "Flat discount"
    : promotion.maximumPromotion
      ? `Up to $${Number(promotion.maximumPromotion).toFixed(2)}`
      : "";

const formatDate = (value) => (value ? moment(value).format("MMM D, YYYY") : "");
const formatTime = (value) => (value ? moment(value).format("hh:mm A") : "");

const readableError = (error) => {
  if (!error) return "";
  if (error.graphQLErrors && error.graphQLErrors.length > 0) {
    return error.graphQLErrors[0].message;
  }
  return error.message || "Something went wrong, please try again.";
};

const copyToClipboard = (text) => {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(text);
  }
  return new Promise((resolve, reject) => {
    try {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      resolve();
    } catch (err) {
      reject(err);
    }
  });
};

// /shopAdmin/addPromotion and /shopAdmin/editPromotion/:id now redirect
// here with a query param instead of rendering their own page, so old
// links and the "Add promotion" shortcuts other screens may still point
// at keep working.
const readDeepLinkParams = () => {
  try {
    return new URLSearchParams(window.location.search);
  } catch (e) {
    return new URLSearchParams();
  }
};

const clearDeepLinkParams = () => {
  window.history.replaceState({}, "", window.location.pathname);
};

/* ------------------------------------------------------------------ *
 * Component
 * ------------------------------------------------------------------ */

const Promotions = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [conditionFilter, setConditionFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [percentFrom, setPercentFrom] = useState("");
  const [percentTo, setPercentTo] = useState("");
  const [amountFrom, setAmountFrom] = useState("");
  const [amountTo, setAmountTo] = useState("");
  const [sortOption, setSortOption] = useState("endingSoon");
  const [pageCount, setPageCount] = useState(0);
  const [perPage, setPerPage] = useState(10);

  const [selectedIds, setSelectedIds] = useState([]);
  const [copiedId, setCopiedId] = useState("");

  const [formOpen, setFormOpen] = useState(false);
  const [formTarget, setFormTarget] = useState(null);
  const [duplicating, setDuplicating] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState("");

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteSomeOpen, setDeleteSomeOpen] = useState(false);
  const [actionError, setActionError] = useState("");

  const { data, loading, error, refetch } = useQuery(GET_PROMOTIONS, {
    variables: { limit: FETCH_LIMIT, skip: 0 },
    fetchPolicy: "network-only",
  });

  const [createPromotion, { loading: creating }] = useMutation(CREATE_PROMOTION);
  const [updatePromotion, { loading: updating }] = useMutation(UPDATE_PROMOTION);
  const [deletePromotions, { loading: deleting }] = useMutation(DELETE_PROMOTIONS);

  const promotions = (data && data.getOrderPromotionsByShopAdmin) || [];
  const totalCount = (data && data.getOrderPromotionsByShopAdminCount) || 0;

  /* ---------------- deep links from /addPromotion and /editPromotion/:id ---------------- */

  useEffect(() => {
    if (!data) return;
    const params = readDeepLinkParams();
    if (params.get("new") === "1") {
      openCreateDialog();
      clearDeepLinkParams();
    } else if (params.get("edit")) {
      const target = promotions.find((p) => p._id === params.get("edit"));
      if (target) openEditDialog(target);
      clearDeepLinkParams();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  /* ---------------- derived list ---------------- */

  const filteredPromotions = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    const filtered = promotions.filter((promotion) => {
      if (term && !String(promotion.promotionCode || "").toLowerCase().includes(term)) {
        return false;
      }
      if (typeFilter && promotion.type !== typeFilter) return false;
      if (conditionFilter && promotion.condition !== conditionFilter) return false;
      if (statusFilter && getPromotionState(promotion).key !== statusFilter) return false;

      if (percentFrom && Number(promotion.percent || 0) < parseFloat(percentFrom)) return false;
      if (percentTo && Number(promotion.percent || 0) > parseFloat(percentTo)) return false;
      if (amountFrom && Number(promotion.maximumPromotion || 0) < parseFloat(amountFrom)) return false;
      if (amountTo && Number(promotion.maximumPromotion || 0) > parseFloat(amountTo)) return false;

      return true;
    });

    const sorted = [...filtered].sort((a, b) => {
      switch (sortOption) {
        case "codeAsc":
          return String(a.promotionCode || "").localeCompare(String(b.promotionCode || ""));
        case "percentDesc":
          return Number(b.percent || 0) - Number(a.percent || 0);
        case "percentAsc":
          return Number(a.percent || 0) - Number(b.percent || 0);
        case "maxDesc":
          return Number(b.maximumPromotion || 0) - Number(a.maximumPromotion || 0);
        case "maxAsc":
          return Number(a.maximumPromotion || 0) - Number(b.maximumPromotion || 0);
        case "endingSoon":
        default: {
          const aTo = a.to ? new Date(a.to).getTime() : Infinity;
          const bTo = b.to ? new Date(b.to).getTime() : Infinity;
          return aTo - bTo;
        }
      }
    });

    return sorted;
  }, [
    promotions,
    searchTerm,
    typeFilter,
    conditionFilter,
    statusFilter,
    percentFrom,
    percentTo,
    amountFrom,
    amountTo,
    sortOption,
  ]);

  const pageStart = pageCount * perPage;
  const currentPagePromotions = filteredPromotions.slice(pageStart, pageStart + perPage);

  /* ---------------- metrics ---------------- */

  const activeCount = promotions.filter((p) => getPromotionState(p).key === "active").length;
  const scheduledCount = promotions.filter((p) => getPromotionState(p).key === "scheduled").length;
  const expiredCount = promotions.filter((p) => getPromotionState(p).key === "expired").length;
  const endingSoonCount = promotions.filter((p) => {
    const state = getPromotionState(p);
    return state.key === "active" && state.status === "warning";
  }).length;

  /* ---------------- filter chips ---------------- */

  const activeFilterChips = [];
  if (searchTerm.trim()) activeFilterChips.push({ id: "search", label: `Code: ${searchTerm}` });
  if (typeFilter) {
    activeFilterChips.push({
      id: "type",
      label: `Type: ${typeFilter === "FIXED" ? "Fixed" : "Percent"}`,
    });
  }
  if (conditionFilter) {
    activeFilterChips.push({ id: "condition", label: `Condition: ${CONDITION_LABELS[conditionFilter]}` });
  }
  if (statusFilter) {
    activeFilterChips.push({
      id: "status",
      label: `Status: ${statusFilter.charAt(0).toUpperCase()}${statusFilter.slice(1)}`,
    });
  }
  if (percentFrom || percentTo) {
    activeFilterChips.push({ id: "percent", label: `Percent: ${percentFrom || "-"} to ${percentTo || "-"}` });
  }
  if (amountFrom || amountTo) {
    activeFilterChips.push({ id: "amount", label: `Max: ${amountFrom || "-"} to ${amountTo || "-"}` });
  }

  const clearFilter = (id) => {
    if (id === "search") setSearchTerm("");
    if (id === "type") setTypeFilter("");
    if (id === "condition") setConditionFilter("");
    if (id === "status") setStatusFilter("");
    if (id === "percent") {
      setPercentFrom("");
      setPercentTo("");
    }
    if (id === "amount") {
      setAmountFrom("");
      setAmountTo("");
    }
    setPageCount(0);
  };

  const clearAllFilters = () => {
    setSearchTerm("");
    setTypeFilter("");
    setConditionFilter("");
    setStatusFilter("");
    setPercentFrom("");
    setPercentTo("");
    setAmountFrom("");
    setAmountTo("");
    setPageCount(0);
  };

  /* ---------------- selection ---------------- */

  const selectedInPage = currentPagePromotions.filter((p) => selectedIds.includes(p._id)).length;
  const checkboxState =
    currentPagePromotions.length === 0
      ? "unchecked"
      : selectedInPage === 0
        ? "unchecked"
        : selectedInPage === currentPagePromotions.length
          ? "checked"
          : "indeterminate";

  const toggleSelectAll = () => {
    if (checkboxState === "checked") {
      setSelectedIds((ids) => ids.filter((id) => !currentPagePromotions.some((p) => p._id === id)));
    } else {
      setSelectedIds((ids) =>
        Array.from(new Set([...ids, ...currentPagePromotions.map((p) => p._id)])),
      );
    }
  };

  const toggleSelectOne = (id) => {
    setSelectedIds((ids) => (ids.includes(id) ? ids.filter((i) => i !== id) : [...ids, id]));
  };

  /* ---------------- form handling ---------------- */

  const setFormValue = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const openCreateDialog = () => {
    setForm(emptyForm());
    setFieldErrors({});
    setFormError("");
    setFormTarget(null);
    setDuplicating(false);
    setFormOpen(true);
  };

  const openEditDialog = (promotion) => {
    setForm({
      promotionCode: promotion.promotionCode || "",
      condition: promotion.condition || "TIMELY",
      type: promotion.type || "PERCENT",
      percent: promotion.percent != null ? String(promotion.percent) : "",
      maximumPromotion: promotion.maximumPromotion != null ? String(promotion.maximumPromotion) : "",
      useLimitCount: promotion.useLimitCount != null ? String(promotion.useLimitCount) : "",
      from: promotion.from ? moment(promotion.from).format("YYYY-MM-DDTHH:mm") : "",
      to: promotion.to ? moment(promotion.to).format("YYYY-MM-DDTHH:mm") : "",
    });
    setFieldErrors({});
    setFormError("");
    setFormTarget(promotion);
    setDuplicating(false);
    setFormOpen(true);
  };

  const openDuplicateDialog = (promotion) => {
    setForm({
      promotionCode: "",
      condition: promotion.condition || "TIMELY",
      type: promotion.type || "PERCENT",
      percent: promotion.percent != null ? String(promotion.percent) : "",
      maximumPromotion: promotion.maximumPromotion != null ? String(promotion.maximumPromotion) : "",
      useLimitCount: promotion.useLimitCount != null ? String(promotion.useLimitCount) : "",
      from: "",
      to: "",
    });
    setFieldErrors({});
    setFormError("");
    setFormTarget(null);
    setDuplicating(true);
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setDuplicating(false);
  };

  const validateForm = () => {
    const errors = {};

    if (!form.promotionCode.trim()) {
      errors.promotionCode = "Give the promotion a code.";
    }

    if (!String(form.useLimitCount).trim() || !/^[1-9]\d*$/.test(String(form.useLimitCount).trim())) {
      errors.useLimitCount = "Must be a whole number greater than 0.";
    }

    if (form.type === "PERCENT") {
      const percent = parseFloat(form.percent);
      if (!form.percent || Number.isNaN(percent) || percent <= 0 || percent > 100) {
        errors.percent = "Enter a percentage between 1 and 100.";
      }
    }

    const maxAmount = parseFloat(form.maximumPromotion);
    if (!form.maximumPromotion || Number.isNaN(maxAmount) || maxAmount <= 0) {
      errors.maximumPromotion =
        form.type === "FIXED" ? "Enter the discount amount." : "Enter a maximum discount amount.";
    }

    if (!form.from) {
      errors.from = "Pick a start date.";
    } else if (!formTarget && moment(form.from).isBefore(moment())) {
      errors.from = "Start date must be in the future.";
    }

    if (!form.to) {
      errors.to = "Pick an end date.";
    } else if (form.from && !moment(form.to).isAfter(moment(form.from))) {
      errors.to = "End date must be after the start date.";
    }

    return errors;
  };

  const handleSubmit = () => {
    if (creating || updating) return;
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    setFormError("");

    const variables = {
      condition: form.condition,
      type: form.type,
      promotionCode: form.promotionCode.trim(),
      percent: form.type === "PERCENT" ? parseFloat(form.percent) : null,
      maximumPromotion: parseFloat(form.maximumPromotion),
      useLimitCount: parseInt(form.useLimitCount, 10),
      from: moment(form.from).utc(),
      to: moment(form.to).utc(),
    };

    const mutate = formTarget
      ? updatePromotion({ variables: { promotionId: formTarget._id, ...variables } })
      : createPromotion({ variables });

    mutate
      .then(() => {
        closeForm();
        return refetch();
      })
      .catch((err) => setFormError(readableError(err)));
  };

  /* ---------------- delete ---------------- */

  const handleDeleteOne = () => {
    if (!deleteTarget || deleting) return;
    deletePromotions({ variables: { idSet: [deleteTarget._id] } })
      .then(() => {
        setDeleteTarget(null);
        return refetch();
      })
      .catch((err) => {
        setDeleteTarget(null);
        setActionError(readableError(err));
      });
  };

  const handleDeleteSelected = () => {
    if (selectedIds.length === 0 || deleting) return;
    deletePromotions({ variables: { idSet: selectedIds } })
      .then(() => {
        setDeleteSomeOpen(false);
        setSelectedIds([]);
        return refetch();
      })
      .catch((err) => {
        setDeleteSomeOpen(false);
        setActionError(readableError(err));
      });
  };

  /* ---------------- copy + export ---------------- */

  const handleCopyCode = (promotion) => {
    copyToClipboard(promotion.promotionCode)
      .then(() => {
        setCopiedId(promotion._id);
        window.setTimeout(() => setCopiedId(""), 1500);
      })
      .catch(() => {});
  };

  const handleExport = () => {
    const rows = [["Code", "Type", "Condition", "Percent", "Maximum", "Use limit", "Starts", "Ends", "Status"]];
    filteredPromotions.forEach((p) => {
      rows.push([
        p.promotionCode,
        p.type,
        CONDITION_LABELS[p.condition] || p.condition,
        p.percent || "-",
        p.maximumPromotion != null ? Number(p.maximumPromotion).toFixed(2) : "-",
        p.useLimitCount,
        p.from ? moment(p.from).format("MM/DD/YYYY HH:mm") : "-",
        p.to ? moment(p.to).format("MM/DD/YYYY HH:mm") : "-",
        getPromotionState(p).label,
      ]);
    });
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, "Promotions");
    XLSX.writeFile(wb, "Promotions.xlsx");
  };

  /* ---------------- render ---------------- */

  if (loading && !data) {
    return (
      <div className="page promotionsPage">
        <main className="main">
          <PageHeader title="Promotions" description="Create and manage discount codes for your customers." />
          <MetricsSkeleton count={4} />
          <TableSkeleton rows={6} columns={7} />
        </main>
      </div>
    );
  }

  if (error) {
    return <ErrorPage />;
  }

  return (
    <div className="page promotionsPage">
      <main className="main">
        {/* HEADER */}
        <PageHeader
          title="Promotions"
          count={totalCount || promotions.length}
          description="Create discount codes, schedule when they run, and track which ones are about to expire."
          actions={
            <React.Fragment>
              <PageActionButton
                icon={<CloudDownloadIcon fontSize="small" />}
                label="Export"
                info={`${filteredPromotions.length} promotion${filteredPromotions.length === 1 ? "" : "s"}`}
                onClick={handleExport}
                disabled={filteredPromotions.length === 0}
              />
              <PageActionButton
                variant="primary"
                icon={<AddIcon fontSize="small" />}
                label="Add promotion"
                info="Create a new code"
                onClick={openCreateDialog}
              />
            </React.Fragment>
          }
        />

        {/* STATS */}
        <section className="stats">
          <MetricCard icon={<LocalOfferIcon />} title="Total promotions" value={totalCount || promotions.length} />
          <MetricCard
            icon={<CheckCircleOutlineIcon />}
            title="Active"
            value={activeCount}
            tone="success"
            hint={endingSoonCount > 0 ? `${endingSoonCount} ending within 3 days` : "None ending soon"}
          />
          <MetricCard icon={<ScheduleIcon />} title="Scheduled" value={scheduledCount} tone="info" />
          <MetricCard icon={<EventBusyIcon />} title="Expired" value={expiredCount} />
        </section>

        {/* FILTERS */}
        <section className="filters">
          <SearchField
            placeholder="Search by promotion code..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPageCount(0);
            }}
          />
          <div className="filtersSelects">
            <FilterDropdown
              label={typeFilter ? `Type: ${typeFilter === "FIXED" ? "Fixed" : "Percent"}` : "Type"}
              options={[{ value: "", label: "All types" }, ...TYPE_OPTIONS]}
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value);
                setPageCount(0);
              }}
            />
            <FilterDropdown
              label={conditionFilter ? `Condition: ${CONDITION_LABELS[conditionFilter]}` : "Condition"}
              options={[{ value: "", label: "All conditions" }, ...CONDITION_OPTIONS]}
              value={conditionFilter}
              onChange={(e) => {
                setConditionFilter(e.target.value);
                setPageCount(0);
              }}
            />
            <FilterDropdown
              label={statusFilter ? `Status: ${statusFilter}` : "Status"}
              options={[
                { value: "", label: "All statuses" },
                { value: "active", label: "Active" },
                { value: "scheduled", label: "Scheduled" },
                { value: "expired", label: "Expired" },
              ]}
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPageCount(0);
              }}
            />
            <NumberRangeFilter
              label="Percent"
              fromValue={percentFrom}
              toValue={percentTo}
              onFromChange={(e) => {
                setPercentFrom(e.target.value);
                setPageCount(0);
              }}
              onToChange={(e) => {
                setPercentTo(e.target.value);
                setPageCount(0);
              }}
            />
            <NumberRangeFilter
              label="Max discount"
              fromValue={amountFrom}
              toValue={amountTo}
              onFromChange={(e) => {
                setAmountFrom(e.target.value);
                setPageCount(0);
              }}
              onToChange={(e) => {
                setAmountTo(e.target.value);
                setPageCount(0);
              }}
            />
            <FilterDropdown
              label={`Sort: ${SORT_LABELS[sortOption]}`}
              options={SORT_OPTIONS}
              value={sortOption}
              active={false}
              align="right"
              onChange={(e) => setSortOption(e.target.value)}
            />
          </div>
        </section>

        {activeFilterChips.length > 0 && (
          <ActiveFilterChipList
            filters={activeFilterChips}
            onClearFilter={clearFilter}
            onClearAll={clearAllFilters}
          />
        )}

        {/* TOOLBAR */}
        <TableToolbar
          selectedCount={selectedIds.length}
          checkboxState={checkboxState}
          onCheckboxChange={toggleSelectAll}
          bulkActions={
            <Button
              variant="dangerQuiet"
              size="sm"
              icon={<DeleteIcon fontSize="small" />}
              onClick={() => setDeleteSomeOpen(true)}
            >
              Delete selected
            </Button>
          }
          rightActions={
            <IconButton
              className="tableTopIconButton"
              size="small"
              onClick={() => refetch()}
              title="Refresh promotions"
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          }
        />

        {actionError && (
          <div className="dsFormMessage error" role="alert">
            {actionError}
          </div>
        )}

        {/* TABLE */}
        <div className="tableResponsive">
          <section className="table">
            <div className="tableHeader">
              <div className="tableHeaderCell tableHeaderCheckbox">
                <Checkbox
                  size="small"
                  checked={checkboxState === "checked"}
                  indeterminate={checkboxState === "indeterminate"}
                  onChange={toggleSelectAll}
                  inputProps={{ "aria-label": "Select all promotions on this page" }}
                />
              </div>
              <div className="tableHeaderCell">Code</div>
              <div className="tableHeaderCell">Discount</div>
              <div className="tableHeaderCell">Condition</div>
              <div className="tableHeaderCell">Status</div>
              <div className="tableHeaderCell">Use limit</div>
              <div className="tableHeaderCell">Valid</div>
              <div className="tableHeaderCell tableHeaderAction">Actions</div>
            </div>

            {currentPagePromotions.length === 0 ? (
              <EmptyState
                icon={<LocalOfferIcon />}
                title={promotions.length === 0 ? "No promotions yet" : "No matches"}
                message={
                  promotions.length === 0
                    ? "Create a discount code to bring customers back."
                    : "No promotions match these filters. Try widening or clearing them."
                }
                action={
                  promotions.length === 0 ? (
                    <Button variant="primary" icon={<AddIcon fontSize="small" />} onClick={openCreateDialog}>
                      Add promotion
                    </Button>
                  ) : (
                    <Button onClick={clearAllFilters}>Clear filters</Button>
                  )
                }
              />
            ) : (
              currentPagePromotions.map((promotion) => {
                const state = getPromotionState(promotion);
                return (
                  <div className="row" key={promotion._id}>
                    <Checkbox
                      size="small"
                      checked={selectedIds.includes(promotion._id)}
                      onChange={() => toggleSelectOne(promotion._id)}
                      inputProps={{ "aria-label": `Select ${promotion.promotionCode}` }}
                    />

                    <div className="promotionCodeCell" data-label="Code">
                      <span className="promotionCode">{promotion.promotionCode}</span>
                      <IconButton
                        size="small"
                        className="rowActionButton"
                        onClick={() => handleCopyCode(promotion)}
                        title="Copy code"
                      >
                        {copiedId === promotion._id ? (
                          <CheckIcon fontSize="small" />
                        ) : (
                          <FileCopyIcon style={{ fontSize: 16 }} />
                        )}
                      </IconButton>
                    </div>

                    <div className="promotionDiscount" data-label="Discount">
                      <h4>{formatDiscount(promotion)}</h4>
                      <p>{formatCap(promotion)}</p>
                    </div>

                    <span data-label="Condition">
                      <span className="promotionConditionTag">
                        {CONDITION_LABELS[promotion.condition] || promotion.condition}
                      </span>
                    </span>

                    <span data-label="Status">
                      <StatusBadge status={state.status} label={state.label} />
                    </span>

                    <span className="cellValue" data-label="Use limit">
                      {promotion.useLimitCount || "-"} / customer
                    </span>

                    <span className="updated" data-label="Valid">
                      <strong>{formatDate(promotion.from)} &rarr; {formatDate(promotion.to)}</strong>
                      <span>
                        {formatTime(promotion.from)} &ndash; {formatTime(promotion.to)}
                      </span>
                    </span>

                    <div className="actions">
                      <IconButton
                        size="small"
                        className="rowActionButton"
                        onClick={() => openEditDialog(promotion)}
                        title="Edit promotion"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        className="rowActionButton"
                        onClick={() => openDuplicateDialog(promotion)}
                        title="Duplicate promotion"
                      >
                        <FileCopyIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        className="rowActionButton"
                        onClick={() => setDeleteTarget(promotion)}
                        title="Delete promotion"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </div>
                  </div>
                );
              })
            )}
          </section>
        </div>

        {/* PAGINATION */}
        {filteredPromotions.length > 0 && (
          <PaginationComponent
            currentPage={pageCount}
            pageSize={perPage}
            totalCount={filteredPromotions.length}
            pageSizeOptions={[10, 20, 50]}
            onPageChange={(newPage) => setPageCount(newPage)}
            onPageSizeChange={(size) => {
              setPerPage(parseInt(size, 10));
              setPageCount(0);
            }}
          />
        )}
      </main>

      {/* CREATE / EDIT / DUPLICATE */}
      <DialogShell
        open={formOpen}
        onClose={closeForm}
        title={formTarget ? "Edit promotion" : duplicating ? "Duplicate promotion" : "Add promotion"}
        subtitle={
          duplicating
            ? "Copied from an existing promotion — set a new code and dates."
            : "Customers apply this code at checkout."
        }
        size="lg"
        actions={[
          { label: "Cancel", onClick: closeForm },
          {
            label: formTarget ? "Save changes" : "Create promotion",
            onClick: handleSubmit,
            variant: "primary",
            loading: creating || updating,
          },
        ]}
      >
        <div className="promotionForm">
          <Field label="Promotion code" required htmlFor="promoCode" error={fieldErrors.promotionCode}>
            <input
              id="promoCode"
              type="text"
              className="dsInput promotionCodeInput"
              value={form.promotionCode}
              placeholder="SAVE20"
              onChange={(e) => setFormValue("promotionCode", e.target.value.toUpperCase())}
            />
          </Field>

          <div className="promotionFormRow">
            <Field label="Condition" required htmlFor="promoCondition">
              <select
                id="promoCondition"
                className="dsInput"
                value={form.condition}
                onChange={(e) => setFormValue("condition", e.target.value)}
              >
                {CONDITION_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Discount type" required htmlFor="promoType">
              <select
                id="promoType"
                className="dsInput"
                value={form.type}
                onChange={(e) => setFormValue("type", e.target.value)}
              >
                {TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <div className="promotionFormRow">
            {form.type === "PERCENT" && (
              <Field
                label="Discount percentage"
                required
                htmlFor="promoPercent"
                error={fieldErrors.percent}
                hint="1-100"
              >
                <input
                  id="promoPercent"
                  type="number"
                  min="1"
                  max="100"
                  className="dsInput"
                  value={form.percent}
                  onChange={(e) => setFormValue("percent", e.target.value)}
                />
              </Field>
            )}
            <Field
              label={form.type === "FIXED" ? "Discount amount" : "Maximum discount"}
              required
              htmlFor="promoMax"
              error={fieldErrors.maximumPromotion}
              hint={form.type === "FIXED" ? "Flat amount taken off the order." : "Caps the percentage discount."}
            >
              <input
                id="promoMax"
                type="number"
                min="0"
                step="0.01"
                className="dsInput"
                value={form.maximumPromotion}
                onChange={(e) => setFormValue("maximumPromotion", e.target.value)}
              />
            </Field>
          </div>

          <Field
            label="Use limit per customer"
            required
            htmlFor="promoLimit"
            error={fieldErrors.useLimitCount}
            hint="How many times one customer may use this code."
          >
            <input
              id="promoLimit"
              type="number"
              min="1"
              className="dsInput"
              value={form.useLimitCount}
              onChange={(e) => setFormValue("useLimitCount", e.target.value)}
            />
          </Field>

          <div className="promotionFormRow">
            <Field label="Starts" required htmlFor="promoFrom" error={fieldErrors.from}>
              <input
                id="promoFrom"
                type="datetime-local"
                className="dsInput"
                value={form.from}
                onChange={(e) => setFormValue("from", e.target.value)}
              />
            </Field>
            <Field label="Ends" required htmlFor="promoTo" error={fieldErrors.to}>
              <input
                id="promoTo"
                type="datetime-local"
                className="dsInput"
                value={form.to}
                onChange={(e) => setFormValue("to", e.target.value)}
              />
            </Field>
          </div>

          {form.promotionCode.trim() && form.maximumPromotion && (
            <div className="promotionPreview">
              <span className="promotionPreviewLabel">Preview</span>
              <div className="promotionPreviewCode">{form.promotionCode.trim()}</div>
              <div className="promotionPreviewText">
                {form.type === "PERCENT"
                  ? `${form.percent || 0}% off, up to $${Number(form.maximumPromotion || 0).toFixed(2)}`
                  : `$${Number(form.maximumPromotion || 0).toFixed(2)} off`}
                {" · "}
                {CONDITION_LABELS[form.condition]}
                {" · "}
                {form.useLimitCount || "?"} use{form.useLimitCount === "1" ? "" : "s"} / customer
              </div>
            </div>
          )}

          {formError && (
            <div className="dsFormMessage error" role="alert">
              {formError}
            </div>
          )}
        </div>
      </DialogShell>

      {/* DELETE ONE */}
      <DialogShell
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        title="Delete promotion"
        size="sm"
        actions={[
          { label: "Cancel", onClick: () => setDeleteTarget(null) },
          { label: "Delete", onClick: handleDeleteOne, variant: "danger", loading: deleting },
        ]}
      >
        {deleteTarget && (
          <div className="promotionConfirmBody">
            <span>
              Delete <strong>{deleteTarget.promotionCode}</strong>? Customers will no longer be able to use it.
            </span>
            <span>This cannot be undone.</span>
          </div>
        )}
      </DialogShell>

      {/* DELETE SELECTED */}
      <DialogShell
        open={deleteSomeOpen}
        onClose={() => setDeleteSomeOpen(false)}
        title={`Delete ${selectedIds.length} promotion${selectedIds.length === 1 ? "" : "s"}`}
        size="sm"
        actions={[
          { label: "Cancel", onClick: () => setDeleteSomeOpen(false) },
          { label: "Delete", onClick: handleDeleteSelected, variant: "danger", loading: deleting },
        ]}
      >
        <div className="promotionConfirmBody">
          <span>
            Delete {selectedIds.length} promotion{selectedIds.length === 1 ? "" : "s"}? Customers will no longer be
            able to use {selectedIds.length === 1 ? "it" : "them"}.
          </span>
          <span>This cannot be undone.</span>
        </div>
      </DialogShell>
    </div>
  );
};

export default Promotions;
